import { ContentSourceFormat, ITemplate, ITemplateStatus, Industry, TemplateStatus, asApiError } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'sonner';
import { ILocationState } from '../../../../models';
import { IAdminUserSessionComponentProps } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { useCallbackRef } from '../../../../models/hooks/useCallbackRef';
import { useEventLogging } from '../../../../models/Logging';
import { IndustryHtmlNewsletterViewModel } from '../../../../viewmodels/AdminViewModels';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { HtmlNewsletterEditor, IHtmlNewsletterEditorComponent } from '../../../../web/components/HtmlNewsletterEditor';
import {
	HtmlNewsletterEditorBody,
	HtmlNewsletterEditorSubjectInput,
} from '../../../../web/components/HtmlNewsletterEditor/presentation';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { NavigationBreadcrumbsBar } from '../../../../web/components/NavigationBreadcrumbsBar';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { DefaultIndustryDropdownOptions, TemplateIndustryOption } from '../../../components/IndustriesDropdown';
import { EditIndustryHtmlNewsletterTemplateHeader } from './presentation';
import { styleSheet } from './styles';

interface IProps extends IAdminUserSessionComponentProps, RouteComponentProps<any>, IErrorMessageComponentProps {
	className?: string;
	readonly?: boolean;
	styles?: StyleDeclarationValue[];
}

const EditIndustryHtmlNewsletterTemplateFc: React.FC<IProps> = props => {
	const { className, styles, userSession, errorMessages, location } = props;
	const [industryNewsletter, setIndustryNewsletter] = React.useState<IndustryHtmlNewsletterViewModel>(null);
	const logger = useEventLogging('EditIndustryHtmlNewsletterTemplate');
	const [subject, setSubject] = React.useState(industryNewsletter?.template.subject || industryNewsletter?.name);

	const editorStyle = React.useRef([styleSheet.editor]).current;
	const editorBodyStyle = React.useRef<React.CSSProperties>({
		height: `calc(100% - ${20}px)`,
		marginTop: 20,
	}).current;

	const [selectedIndustryOptions, setSelectedIndustryOptions] =
		React.useState<ISelectOption<TemplateIndustryOption>[]>(null);
	React.useEffect(() => {
		const locationState: ILocationState<IndustryHtmlNewsletterViewModel, any> = location?.state;
		const setInitialTemplate = (vm: IndustryHtmlNewsletterViewModel) => {
			setIndustryNewsletter(vm);
			setSelectedIndustryOptions(
				DefaultIndustryDropdownOptions.filter(x => vm.industries?.includes(x.dataContext as Industry))
			);
			setSubject(vm?.template?.subject || vm?.name);
		};
		if (locationState?.viewModel) {
			locationState.viewModel
				.load()
				?.then(() => {
					logger.logEvent('LoadTemplate', { id: locationState.viewModel.id });
					setInitialTemplate(locationState.viewModel);
				})
				?.catch(error => {
					logger.logApiError('LoadTemplate-Error', error);
					errorMessages.pushApiError(error);
				});
		} else {
			logger.logEvent('CreateBlankTemplate');
			IndustryHtmlNewsletterViewModel.createWithDefaultBlankTemplate(userSession)
				.then(vm => {
					setInitialTemplate(vm);
				})
				.catch(error => {
					logger.logApiError('CreateBlankTemplate-Error', error);
					errorMessages.pushApiError(error);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const locationName = !industryNewsletter
		? 'Loading...'
		: `${industryNewsletter.template?.id ? 'Edit' : 'Create New'} Html Newsletter`;

	const [editor, onEditorCallbackRef] = useCallbackRef<IHtmlNewsletterEditorComponent>();

	const save = React.useCallback(
		async (value: string, publish = false, showToast = false) => {
			let errorMessage: string = null;
			if (selectedIndustryOptions.length === 0) {
				errorMessage = 'You must select at least one industry.';
			}

			const editorContent = await editor?.getContent();
			if (!editorContent) {
				errorMessage = 'Cannot read editor content';
			}
			if (errorMessage) {
				errorMessages.push({
					messages: [errorMessage],
				});
				return;
			}

			const statuses: ITemplateStatus[] = [];
			let selectedIndustries = selectedIndustryOptions.map(x => x.dataContext);
			if (selectedIndustries.includes('All')) {
				selectedIndustries = DefaultIndustryDropdownOptions.filter(x => x.dataContext !== 'All').map(
					x => x.dataContext
				);
			}
			selectedIndustries.forEach(x => {
				statuses.push({
					category: IndustryHtmlNewsletterViewModel.HtmlNewslettersCategoryName,
					industry: x as Industry,
					status: publish ? TemplateStatus.Published : TemplateStatus.Draft,
				});
			});

			const statusesToRemove: ITemplateStatus[] = [];
			const statusesToAdd: ITemplateStatus[] = [];
			const statusesToUpdate: ITemplateStatus[] = [];

			statuses.forEach(x => {
				const match = industryNewsletter.statuses.find(y => y.industry === x.industry && y.category === x.category);
				if (!!match && match.status !== x.status) {
					statusesToUpdate.push(x);
				} else if (!match) {
					statusesToAdd.push(x);
				}
			});

			industryNewsletter.statuses.forEach(x => {
				const match = statuses.find(y => y.industry === x.industry && y.category === x.category);
				if (!match) {
					statusesToRemove.push(x);
				}
			});

			const template: ITemplate = {
				...(industryNewsletter.template || {}),
				subject: value,
				content: {
					document: editorContent.html,
					documentVersion: 1,
					source: JSON.stringify(editorContent.design),
					sourceFormat: ContentSourceFormat.UnlayerHtmlNewsletter,
				},
			};

			try {
				await industryNewsletter.edit(template, statusesToRemove, statusesToAdd, statusesToUpdate);
				if (showToast) {
					toast.success(`Template ${publish ? 'Published' : 'Saved'}!`);
				}
			} catch (err) {
				const error = asApiError(err);
				errorMessages.pushApiError(error);
				logger.logApiError('EditTemplate-Error', error);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedIndustryOptions, editor]
	);

	return (
		<div
			className={`${css(styleSheet.container, ...(styles || []))} edit-industry-html-newsletter-template ${
				className || ''
			}`}
		>
			<MultiContainerHeader
				appBarHeader={<NavigationBreadcrumbsBar currentLocationName={locationName} />}
				fullscreenHeader={locationName}
			/>
			{industryNewsletter ? (
				<HtmlNewsletterEditor
					subject={subject}
					newsletter={industryNewsletter}
					onRef={onEditorCallbackRef}
					styles={editorStyle}
				>
					<EditIndustryHtmlNewsletterTemplateHeader
						industryNewsletter={industryNewsletter}
						save={(publish, showToast) => save(subject, publish, showToast)}
						selectedIndustryOptions={selectedIndustryOptions}
						setSelectedIndustryOptions={setSelectedIndustryOptions}
					/>
					<HtmlNewsletterEditorSubjectInput value={subject} onChange={setSubject} />
					<HtmlNewsletterEditorBody style={editorBodyStyle} />
				</HtmlNewsletterEditor>
			) : (
				<LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />
			)}
		</div>
	);
};

const EditIndustryHtmlNewsletterTemplateAsObserver = observer(EditIndustryHtmlNewsletterTemplateFc);
const EditIndustryHtmlNewsletterTemplateWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey
)(EditIndustryHtmlNewsletterTemplateAsObserver);
export const EditIndustryHtmlNewsletterTemplate = withRouter(EditIndustryHtmlNewsletterTemplateWithContext);
